// eslint-disable-next-line no-unused-vars
import { createSlice, createAsyncThunk, current } from '@reduxjs/toolkit';
import { requestStarted, requestFinished } from '~/stores/redux/common/loadingSlice';
import exclusiveOfferApi from './exclusiveOffer.api';
import { alertSwal } from '~/utils/helpers';
import { handleThunkException } from '~/services/thunk.service';

export const fetchExclusiveOfferApi = createAsyncThunk(
    'shopSaga/exclusiveOffer/fetchExclusiveOfferApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const data = await exclusiveOfferApi.index();
            if (data) {
                dispatch(requestFinished());
                return data;
            }
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        }
    },
);

export const createExclusiveOfferApi = createAsyncThunk(
    'shopSaga/exclusiveOffer/createExclusiveOfferApi',
    async (navigate, { dispatch, rejectWithValue }) => {
        try {
            dispatch(requestStarted());
            const { data } = await exclusiveOfferApi.create();

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const storeExclusiveOfferApi = createAsyncThunk(
    'shopSaga/exclusiveOffer/storeExclusiveOfferApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { navigate, values } = params;

        try {
            dispatch(requestStarted());
            const exclusiveOffer = await exclusiveOfferApi.store(values);
            alertSwal('success', 'ExclusiveOffer created successfully!');

            return exclusiveOffer;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const showExclusiveOfferApi = createAsyncThunk(
    'shopSaga/exclusiveOffer/showExclusiveOfferApi',
    async (params, { dispatch, rejectWithValue }) => {
        const { id, navigate } = params;

        try {
            dispatch(requestStarted());
            const data = await exclusiveOfferApi.show(id);

            return data;
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue();
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const updateExclusiveOfferApi = createAsyncThunk(
    'shopSaga/exclusiveOffer/updateExclusiveOfferApi',
    async (params, { dispatch, getState, rejectWithValue }) => {
        const { navigate, id, values } = params;
        const state = getState();

        try {
            dispatch(requestStarted());
            await exclusiveOfferApi.update(id, values);
            alertSwal('success', 'ExclusiveOffer updated successfully!');

            const { ranks } = state.shopSaga.exclusiveOfferMetaData;
            const updatedRank = ranks.find((item) => item.id === values.rankId);

            return { values, updatedRank };
        } catch (error) {
            handleThunkException(error, dispatch, navigate);

            return rejectWithValue([]);
        } finally {
            dispatch(requestFinished());
        }
    },
);

export const deleteExclusiveOfferApi = createAsyncThunk('shopSaga/exclusiveOffer/deleteExclusiveOfferApi', async (params, { dispatch }) => {
    const { navigate, id } = params;

    try {
        await exclusiveOfferApi.delete(id);

        return id;
    } catch (error) {
        handleThunkException(error, dispatch, navigate);
    }
});

export const exclusiveOfferSlice = createSlice({
    name: 'shopSaga/exclusiveOffer',
    initialState: { rows: [], model: {}, editedModel: {}, pictureAttributeNames: [] }, //model is initialValues that are used for formik.
    reducers: {
        clearEditedExclusiveOffer(state) {
            state.editedModel = {};
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchExclusiveOfferApi.fulfilled, (state, { payload }) => {
                return {
                    ...state,
                    rows: payload,
                };
            })
            .addCase(fetchExclusiveOfferApi.rejected, (state, { payload = [] }) => {
                state.rows = payload;
            });

        builder
            .addCase(createExclusiveOfferApi.fulfilled, (state, { payload }) => {
                state.model = payload.model;
                state.pictureAttributeNames = payload.pictureAttributeNames;
            })
            .addCase(createExclusiveOfferApi.rejected, () => {
                //
            });

        builder
            .addCase(storeExclusiveOfferApi.fulfilled, (state) => {
                return {
                    ...state,
                    rows: [], //clear all rows so that api fetchAll will be called again in index page.
                };
            })
            .addCase(storeExclusiveOfferApi.rejected, () => {
                //do nothing so that state will remain intact and component will not re-render.
            });

        builder
            .addCase(showExclusiveOfferApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload;
            })
            .addCase(showExclusiveOfferApi.rejected, () => {
                //
            });

        builder
            .addCase(updateExclusiveOfferApi.fulfilled, (state, { payload }) => {
                state.editedModel = payload; //this is to make Update component re-render so that the checkDuplicateName() will have the latest `initialName`.

                if (state.rows.length === 0) return;

                const updatedRow = state.rows.find((row) => row.id === payload.values.id);
                updatedRow.name = payload.values.name;
                updatedRow.statPoint = payload.values.statPoint;
                updatedRow.rank = payload.updatedRank.name;
            })
            .addCase(updateExclusiveOfferApi.rejected, () => {
                //
            });

        builder.addCase(deleteExclusiveOfferApi.fulfilled, (state, { payload }) => {
            return {
                ...state,
                rows: state.rows.filter(({ id }) => id !== payload),
            };
        });
    },
});

export const { clearEditedExclusiveOffer } = exclusiveOfferSlice.actions;
